/**
 * ForecastCard.tsx — Sales forecast based on current month's run rate.
 * Formula:
 *   daily_rate  = total_vendas / days_elapsed
 *   forecast    = daily_rate * total_days_in_month
 *   days_to_goal = falta_vender / daily_rate
 * Pure view component — receives all derived values via props.
 */
import { formatBRL, getThermometerColor } from '../../services/salesService';

interface ForecastCardProps {
  totalVendas: number;
  metaMensal: number;
  yearMonth: string;   // YYYY-MM
  loading?: boolean;
}

function getDaysInfo(yearMonth: string) {
  const [year, month] = yearMonth.split('-').map(Number);
  const today         = new Date();
  const isCurrentMonth =
    today.getFullYear() === year && today.getMonth() + 1 === month;

  const totalDays    = new Date(year, month, 0).getDate();
  const daysElapsed  = isCurrentMonth ? today.getDate() : totalDays;
  const daysLeft     = isCurrentMonth ? totalDays - today.getDate() : 0;

  return { totalDays, daysElapsed, daysLeft };
}

export default function ForecastCard({
  totalVendas,
  metaMensal,
  yearMonth,
  loading = false,
}: ForecastCardProps) {
  const { totalDays, daysElapsed, daysLeft } = getDaysInfo(yearMonth);

  const dailyRate  = daysElapsed > 0 ? totalVendas / daysElapsed : 0;
  const forecast   = dailyRate * totalDays;
  const faltaVender = Math.max(metaMensal - totalVendas, 0);

  // Days until goal is reached at current rate
  const daysToGoal =
    dailyRate > 0 && faltaVender > 0 ? Math.ceil(faltaVender / dailyRate) : null;

  const isOnTrack   = forecast >= metaMensal;
  const forecastPct = metaMensal > 0 ? (forecast / metaMensal) * 100 : 0;
  const color       = getThermometerColor(forecastPct);

  const Row = ({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) => (
    <div className="flex justify-between items-baseline">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`text-sm font-semibold ${accent ? 'text-primary-600' : 'text-gray-800 dark:text-gray-200'}`}>
        {value}
      </span>
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-800 dark:text-white">
          🔮 Previsão de Fechamento
        </h2>
        <span
          className="text-xs font-bold px-2 py-0.5 rounded-full"
          style={{ backgroundColor: `${color}20`, color }}
        >
          {isOnTrack ? '✅ No ritmo' : '⚠️ Abaixo'}
        </span>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
          ))}
        </div>
      ) : (
        <>
          {/* Forecast amount — prominent */}
          <div className="text-center py-3 rounded-lg" style={{ backgroundColor: `${color}15` }}>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Previsão para o mês com ritmo atual
            </p>
            <p className="text-3xl font-bold" style={{ color }}>
              {formatBRL(forecast)}
            </p>
            <p className="text-xs mt-1" style={{ color }}>
              {forecastPct.toFixed(1)}% da meta
            </p>
          </div>

          {/* Detail rows */}
          <div className="space-y-2">
            <Row label="Média diária atual"  value={formatBRL(dailyRate)} accent />
            <Row label={`Dias decorridos`}   value={`${daysElapsed}/${totalDays} dias`} />
            <Row label="Dias restantes"      value={`${daysLeft} dia${daysLeft !== 1 ? 's' : ''}`} />
            {daysToGoal !== null && (
              <Row
                label="Dias para atingir meta"
                value={daysToGoal <= daysLeft ? `~${daysToGoal} dia${daysToGoal !== 1 ? 's' : ''}` : '⚠️ Meta difícil'}
                accent
              />
            )}
            {!daysToGoal && faltaVender === 0 && metaMensal > 0 && (
              <Row label="Dias para atingir meta" value="🎉 Já atingida!" accent />
            )}
          </div>
        </>
      )}
    </div>
  );
}
